import * as React from "react";
import { 
  Database, Shield, Key, Server, CheckCircle, XCircle, AlertTriangle,
  Loader2, Eye, EyeOff, Info, RefreshCw, Lock, ExternalLink, Copy, Check, Power
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PermissionCard = ({ permission, isGranted }) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg border ${
    isGranted ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
  }`}>
    {isGranted ? (
      <CheckCircle className="h-5 w-5 text-emerald-500" />
    ) : (
      <XCircle className="h-5 w-5 text-slate-400" />
    )}
    <span className={`font-medium ${isGranted ? "text-emerald-700" : "text-slate-600"}`}>
      {permission}
    </span>
  </div>
);

export const DatabricksSettings = () => {
  const [settings, setSettings] = React.useState(null);
  const [permissions, setPermissions] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isTesting, setIsTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);
  const [isEnabled, setIsEnabled] = React.useState(true);
  
  // Form state
  const [host, setHost] = React.useState("");
  const [token, setToken] = React.useState("");
  const [warehouseId, setWarehouseId] = React.useState("");
  const [showToken, setShowToken] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [copiedStep, setCopiedStep] = React.useState(null);

  React.useEffect(() => {
    loadSettings();
    loadPermissions();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/databricks/settings`, { withCredentials: true });
      setSettings(response.data);
      setHost(response.data.host || "");
      setWarehouseId(response.data.warehouse_id || "");
      setIsEnabled(response.data.is_enabled !== false);
      // Don't set token - it's masked
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Error al cargar configuración de Databricks");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    try {
      await axios.put(`${API}/admin/databricks/toggle`, { enabled }, { withCredentials: true });
      setIsEnabled(enabled);
      toast.success(enabled ? "Lakehouse habilitado" : "Lakehouse deshabilitado");
    } catch (error) {
      console.error("Error toggling Lakehouse:", error);
      toast.error("Error al cambiar estado de Lakehouse");
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/databricks/required-permissions`, { withCredentials: true });
      setPermissions(response.data);
    } catch (error) {
      console.error("Error loading permissions:", error);
    }
  };

  const handleSave = async () => {
    if (!host.trim()) {
      toast.error("El host de Databricks es requerido");
      return;
    }
    if (!token.trim()) {
      toast.error("El token de acceso es requerido");
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(
        `${API}/admin/databricks/settings`,
        { host: host.trim(), token: token.trim(), warehouse_id: warehouseId.trim() },
        { withCredentials: true }
      );
      
      toast.success("Credenciales actualizadas correctamente");
      setToken(""); // Clear the token field for security
      setHasChanges(false);
      setTestResult(null);
      await loadSettings();
    } catch (error) {
      toast.error("Error al guardar", {
        description: error.response?.data?.detail || "No se pudieron actualizar las credenciales"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      // If there are unsaved changes, test with the new credentials
      const testData = hasChanges && token ? {
        host: host.trim(),
        token: token.trim(),
        warehouse_id: warehouseId.trim()
      } : null;

      const response = await axios.post(
        `${API}/admin/databricks/test`,
        testData,
        { withCredentials: true }
      );
      
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success("Conexión exitosa");
      } else {
        toast.warning("Conexión con problemas", {
          description: response.data.message
        });
      }
    } catch (error) {
      toast.error("Error al probar conexión", {
        description: error.response?.data?.detail || "No se pudo probar la conexión"
      });
      setTestResult({
        success: false,
        message: error.response?.data?.detail || "Error al probar la conexión"
      });
    } finally {
      setIsTesting(false);
    }
  };

  const copyToClipboard = (text, stepIndex) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(stepIndex);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable Switch */}
      <div className={`flex items-center justify-between p-4 rounded-lg border ${
        isEnabled ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
      }`}>
        <div className="flex items-center gap-3">
          <Power className={`h-5 w-5 ${isEnabled ? 'text-blue-600' : 'text-slate-400'}`} />
          <div>
            <p className="font-medium text-slate-800">
              {isEnabled ? 'Lakehouse Habilitado' : 'Lakehouse Deshabilitado'}
            </p>
            <p className="text-sm text-slate-500">
              {isEnabled 
                ? 'Los módulos pueden usar esta conexión como fallback' 
                : 'Ningún módulo puede usar esta conexión (ahorro de costos)'}
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
        />
      </div>

      <div className={`space-y-6 ${!isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Header Alert */}
        <Alert className="bg-amber-50 border-amber-200">
          <Shield className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Información Sensible</AlertTitle>
          <AlertDescription className="text-amber-700">
            Las credenciales de Databricks son información sensible. El token de acceso se almacena 
            de forma encriptada y nunca se muestra completo. Asegúrate de usar un token con permisos 
            limitados y fecha de expiración.
          </AlertDescription>
        </Alert>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Configuration Form */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-purple-500" />
                <CardTitle>Configuración de Databricks</CardTitle>
              </div>
              <CardDescription>
                Configura las credenciales para conectar con tu workspace de Databricks
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Status */}
              {settings && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  {settings.is_configured ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      <span className="text-sm text-emerald-700">Configurado</span>
                      {settings.last_updated_by && (
                        <span className="text-xs text-slate-500 ml-auto">
                          Última actualización: {settings.last_updated_by}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      <span className="text-sm text-amber-700">No configurado</span>
                    </>
                  )}
                </div>
              )}

              {/* Host */}
            <div className="space-y-2">
              <Label htmlFor="host" className="flex items-center gap-2">
                <Server className="h-4 w-4 text-slate-500" />
                Host de Databricks
              </Label>
              <Input
                id="host"
                type="text"
                placeholder="dbc-xxxxx-xxxx.cloud.databricks.com"
                value={host}
                onChange={(e) => {
                  setHost(e.target.value);
                  setHasChanges(true);
                }}
              />
              <p className="text-xs text-slate-500">
                URL de tu workspace sin &quot;https://&quot; (ej: dbc-xxxxx.cloud.databricks.com)
              </p>
            </div>

            {/* Token */}
            <div className="space-y-2">
              <Label htmlFor="token" className="flex items-center gap-2">
                <Key className="h-4 w-4 text-slate-500" />
                Token de Acceso Personal
              </Label>
              <div className="relative">
                <Input
                  id="token"
                  type={showToken ? "text" : "password"}
                  placeholder={settings?.token_masked || "dapi..."}
                  value={token}
                  onChange={(e) => {
                    setToken(e.target.value);
                    setHasChanges(true);
                  }}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {settings?.token_masked && !token && (
                <p className="text-xs text-slate-500">
                  Token actual: <code className="bg-slate-100 px-1 rounded">{settings.token_masked}</code>
                  <span className="ml-1">(ingresa uno nuevo para reemplazarlo)</span>
                </p>
              )}
            </div>

            {/* Warehouse ID (optional) */}
            <div className="space-y-2">
              <Label htmlFor="warehouse" className="flex items-center gap-2">
                <Database className="h-4 w-4 text-slate-500" />
                Warehouse ID <Badge variant="outline" className="ml-2 text-xs">Opcional</Badge>
              </Label>
              <Input
                id="warehouse"
                type="text"
                placeholder="xxxxxxxxxxxxxxxx"
                value={warehouseId}
                onChange={(e) => {
                  setWarehouseId(e.target.value);
                  setHasChanges(true);
                }}
              />
              <p className="text-xs text-slate-500">
                ID del SQL Warehouse a usar (se puede dejar vacío para usar el predeterminado)
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleTest}
                variant="outline"
                disabled={isTesting || (!settings?.is_configured && !hasChanges)}
                className="flex-1"
              >
                {isTesting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Probar Conexión
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || !hasChanges || !token}
                className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-500"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Lock className="h-4 w-4 mr-2" />
                )}
                Guardar Credenciales
              </Button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-4 rounded-lg border ${
                testResult.success 
                  ? "bg-emerald-50 border-emerald-200" 
                  : "bg-amber-50 border-amber-200"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                  )}
                  <span className={`font-medium ${testResult.success ? "text-emerald-700" : "text-amber-700"}`}>
                    {testResult.success ? "Conexión Exitosa" : "Conexión con Problemas"}
                  </span>
                </div>
                <p className={`text-sm ${testResult.success ? "text-emerald-600" : "text-amber-600"}`}>
                  {testResult.message}
                </p>
                
                {testResult.permissions && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <PermissionCard permission="Conectar" isGranted={testResult.permissions.connect} />
                    <PermissionCard permission="SQL Warehouse" isGranted={testResult.permissions.list_warehouses} />
                    <PermissionCard permission="Ejecutar SQL" isGranted={testResult.permissions.execute_sql} />
                    <PermissionCard permission="Crear Jobs" isGranted={testResult.permissions.create_jobs} />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Information Panel */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-lg">¿Qué son estas credenciales?</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-slate-600">
              <p>
                Las credenciales de Databricks permiten a esta aplicación conectarse a tu workspace 
                para ejecutar consultas SQL y crear trabajos programados (alertas).
              </p>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">⚠️ Recomendaciones de Seguridad:</h4>
                <ul className="list-disc pl-5 space-y-1 text-blue-700">
                  <li>Usa un <strong>Service Principal</strong> o usuario dedicado</li>
                  <li>No uses el token del owner o admin del workspace</li>
                  <li>Configura una <strong>fecha de expiración</strong> (90 días recomendado)</li>
                  <li>Otorga solo los <strong>permisos mínimos necesarios</strong></li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Required Permissions Accordion */}
          {permissions && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-purple-500" />
                  <CardTitle className="text-lg">Permisos Requeridos</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {permissions.required_permissions.map((perm, idx) => (
                    <AccordionItem key={idx} value={`perm-${idx}`}>
                      <AccordionTrigger className="text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">
                            {perm.permission}
                          </Badge>
                          <span>{perm.resource}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-600">
                        <p className="mb-2">{perm.description}</p>
                        <div className="bg-slate-50 p-3 rounded text-xs">
                          <strong>Cómo otorgar:</strong> {perm.how_to_grant}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          )}

          {/* Token Creation Steps */}
          {permissions && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-amber-500" />
                  <CardTitle className="text-lg">Cómo Crear el Token</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3">
                  {permissions.token_creation_steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-sm font-medium flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="text-sm text-slate-600 flex-1">{step.replace(/^\d+\.\s*/, '')}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-4">
                  <Button variant="outline" size="sm" className="gap-2" asChild>
                    <a 
                      href="https://docs.databricks.com/en/dev-tools/auth/pat.html" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver documentación oficial
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};
